import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import PrintDialog from '@/components/shared/PrintDialog';
import FinishedGoodsQCReportPrintContent from './FinishedGoodsQCReportPrintContent';
import { useCompanyConfig } from '@/hooks/useCompanyConfig';
import { Loader2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';

interface FinishedGoodsQCReportPrintModalProps {
  open: boolean;
  onClose: () => void;
}

type DatePreset = 'today' | 'yesterday' | 'thisMonth' | 'custom';

function getLocalDateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function FinishedGoodsQCReportPrintModal({
  open,
  onClose,
}: FinishedGoodsQCReportPrintModalProps) {
  const companyConfig = useCompanyConfig(open);

  // Preset calculation
  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => getLocalDateString(today), [today]);
  
  const yesterdayStr = useMemo(() => {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return getLocalDateString(y);
  }, []);

  const firstDayOfMonthStr = useMemo(() => {
    const f = new Date();
    f.setDate(1);
    return getLocalDateString(f);
  }, []);

  // Filter States
  const [preset, setPreset] = useState<DatePreset>('thisMonth');
  const [dateFrom, setDateFrom] = useState<string>(firstDayOfMonthStr);
  const [dateTo, setDateTo] = useState<string>(todayStr);
  const [groupBy, setGroupBy] = useState<'date' | 'customer' | 'none'>('date');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [customerId, setCustomerId] = useState<string>('');

  const handleSelectPreset = (p: DatePreset) => {
    setPreset(p);
    if (p === 'today') {
      setDateFrom(todayStr);
      setDateTo(todayStr);
    } else if (p === 'yesterday') {
      setDateFrom(yesterdayStr);
      setDateTo(yesterdayStr);
    } else if (p === 'thisMonth') {
      setDateFrom(firstDayOfMonthStr);
      setDateTo(todayStr);
    }
  };

  // Fetch report data
  const { data: reportData, isLoading, isFetching } = useQuery({
    queryKey: ['qc-final-finished-goods-report', dateFrom, dateTo, customerId],
    queryFn: async () => {
      return (trpcClient as any).qcFinal.getFinishedGoodsReport.query({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        customerId: customerId || undefined,
      });
    },
    enabled: open,
    staleTime: 1000 * 60, // 1 min cache
  });

  // Handle Excel Export
  const handleExportExcel = () => {
    if (!reportData?.items || reportData.items.length === 0) {
      toast.error('Không có dữ liệu để xuất Excel');
      return;
    }

    try {
      const rows = reportData.items.map((item: any, idx: number) => {
        const row: Record<string, any> = {
          'STT': idx + 1,
          'Ngày KCS': item.checkTime ? new Date(item.checkTime).toLocaleDateString('vi-VN') : '',
          'Mã LSX': item.poCode || '',
          'Khách Hàng': item.customerName || '',
          'Mã Sản Phẩm': item.productCode || '',
          'Tên Sản Phẩm': item.productName || '',
          'ĐVT': item.unit || '',
          'SL Đạt (KCS)': item.qtyPacked || 0,
        };

        if (reportData.canSeePrice) {
          row['Đơn Giá'] = item.unitPrice || 0;
          row['Thành Tiền'] = item.totalAmount || 0;
        }

        row['Lũy Kế Đạt'] = item.cumulativeQty || 0;
        row['SL Đơn Hàng'] = item.qtyOrder || 0;
        row['SL Kế Hoạch'] = item.qtyPlan || 0;
        row['SL Còn Lại'] = item.remainingQty || 0;

        if (reportData.canSeePrice) {
          row['Tiền Còn Lại'] = item.remainingAmount || 0;
        }

        row['Người Kiểm'] = item.inspectorName || '';
        row['Ghi Chú'] = item.notes || '';
        return row;
      });

      // Add summary line
      const summaryRow: Record<string, any> = {
        'STT': 'TỔNG CỘNG',
        'Ngày KCS': '',
        'Mã LSX': '',
        'Khách Hàng': '',
        'Mã Sản Phẩm': '',
        'Tên Sản Phẩm': '',
        'ĐVT': '',
        'SL Đạt (KCS)': reportData.summary?.totalPackedQty || 0,
      };

      if (reportData.canSeePrice) {
        summaryRow['Đơn Giá'] = '';
        summaryRow['Thành Tiền'] = reportData.summary?.totalPackedAmount || 0;
      }

      summaryRow['Lũy Kế Đạt'] = '';
      summaryRow['SL Đơn Hàng'] = '';
      summaryRow['SL Kế Hoạch'] = '';
      summaryRow['SL Còn Lại'] = reportData.summary?.totalRemainingQty || 0;

      if (reportData.canSeePrice) {
        summaryRow['Tiền Còn Lại'] = reportData.summary?.totalRemainingAmount || 0;
      }

      rows.push(summaryRow);

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Báo Cáo KCS & TP');
      XLSX.writeFile(
        workbook,
        `Bao_Cao_KCS_Thanh_Pham_${dateFrom}_${dateTo}_${Date.now()}.xlsx`
      );
      toast.success('Đã xuất file Excel thành công!');
    } catch (err: any) {
      toast.error('Lỗi khi xuất file Excel: ' + (err.message || ''));
    }
  };

  if (!open) return null;

  // Custom Toolbar Controls rendered inside PrintDialog header
  const extraControls = (
    <div className="hidden lg:flex items-center gap-2 text-xs">
      {/* Date preset selector */}
      <div className="flex items-center bg-slate-100 p-0.5 rounded-md border border-slate-200">
        <button
          type="button"
          onClick={() => handleSelectPreset('today')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'today' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Hôm nay
        </button>
        <button
          type="button"
          onClick={() => handleSelectPreset('yesterday')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'yesterday' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Hôm qua
        </button>
        <button
          type="button"
          onClick={() => handleSelectPreset('thisMonth')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'thisMonth' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tháng này
        </button>
        <button
          type="button"
          onClick={() => setPreset('custom')}
          className={`px-2 py-1 rounded font-medium transition-colors ${
            preset === 'custom' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Tùy chọn
        </button>
      </div>

      {/* Date inputs if custom or for inspection */}
      <div className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 text-[11px]">
        <span className="text-slate-500">Từ:</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPreset('custom');
          }}
          className="bg-transparent border-0 p-0 text-slate-800 text-[11px] focus:outline-hidden cursor-pointer"
        />
        <span className="text-slate-500">Đến:</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPreset('custom');
          }}
          className="bg-transparent border-0 p-0 text-slate-800 text-[11px] focus:outline-hidden cursor-pointer"
        />
      </div>

      {/* Group by selector */}
      <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded border border-slate-200">
        <span className="text-slate-500 font-medium">Nhóm:</span>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as any)}
          className="bg-transparent border-0 text-slate-800 font-medium cursor-pointer text-xs focus:outline-hidden"
        >
          <option value="date">Theo ngày</option>
          <option value="customer">Theo khách hàng</option>
          <option value="none">Không nhóm</option>
        </select>
      </div>

      {/* Orientation selector */}
      <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded border border-slate-200">
        <span className="text-slate-500 font-medium">Khổ giấy:</span>
        <select
          value={orientation}
          onChange={(e) => setOrientation(e.target.value as any)}
          className="bg-transparent border-0 text-slate-800 font-medium cursor-pointer text-xs focus:outline-hidden"
        >
          <option value="portrait">Đứng (Portrait)</option>
          <option value="landscape">Ngang (Landscape)</option>
        </select>
      </div>

      {/* Excel Export Button */}
      <button
        type="button"
        onClick={handleExportExcel}
        className="flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 px-2.5 py-1.5 rounded font-medium transition-colors cursor-pointer"
        title="Xuất bảng số liệu ra file Excel"
      >
        <Download className="h-3.5 w-3.5 text-emerald-600" />
        <span>Xuất Excel</span>
      </button>
    </div>
  );

  return (
    <PrintDialog
      open={open}
      onClose={onClose}
      type="custom"
      orientation={orientation}
      documentTitle={`Bao_Cao_KCS_Thanh_Pham_${dateFrom}_${dateTo}`}
      extraControls={extraControls}
    >
      {isLoading || isFetching ? (
        <div className="py-24 flex flex-col items-center justify-center text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
          <p className="font-medium text-sm">Đang tổng hợp báo cáo KCS &amp; Thành phẩm...</p>
        </div>
      ) : (
        <FinishedGoodsQCReportPrintContent
          reportData={reportData || { summary: {}, items: [], canSeePrice: false }}
          dateRange={{ from: dateFrom, to: dateTo }}
          config={companyConfig}
          orientation={orientation}
          groupBy={groupBy}
        />
      )}
    </PrintDialog>
  );
}

export default FinishedGoodsQCReportPrintModal;
